import { useEffect, useState, useRef } from 'react';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';

interface BoostedCreator {
  _id: string;
  userId: string;
  name: string;
  order: number;
  addedBy?: string;
  createdAt: string;
}

interface SearchResult {
  userId: string;
  name: string;
  email?: string;
  avatar?: string;
}

export default function BoostedCreatorsPage() {
  const [creators, setCreators] = useState<BoostedCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drag state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  // Closing the search dialog drops the query with it, so Escape, the backdrop
  // click and the Cancel button all share one handler.
  const closeSearchModal = () => { setShowModal(false); setSearchQuery(''); setSearchResults([]); };
  useEscapeToClose(showModal, closeSearchModal);

  const fetchBoostedCreators = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/admin/boosted-creators');
      setCreators(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch boosted creators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoostedCreators();
  }, []);

  // Search users with debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        setSearching(true);
        const response = await apiRequest(
          `/api/admin/notifications/search-users?q=${encodeURIComponent(value.trim())}`
        );
        setSearchResults(response.data || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleAddCreator = async (user: SearchResult) => {
    // Check if already in list
    if (creators.some(c => c.userId === user.userId)) {
      alert('This creator is already in the boost list');
      return;
    }

    try {
      setAdding(user.userId);
      await apiRequest('/api/admin/boosted-creators', {
        method: 'POST',
        body: JSON.stringify({ userId: user.userId }),
      });
      setShowModal(false);
      setSearchQuery('');
      setSearchResults([]);
      await fetchBoostedCreators();
    } catch (err: any) {
      alert(err.message || 'Failed to add creator');
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (userId: string) => {
    if (!window.confirm('Remove this creator from the boost list?')) return;

    try {
      setProcessingId(userId);
      await apiRequest(`/api/admin/boosted-creators/${userId}`, { method: 'DELETE' });
      await fetchBoostedCreators();
    } catch (err: any) {
      alert(err.message || 'Failed to remove creator');
    } finally {
      setProcessingId(null);
    }
  };

  // Drag and drop reorder
  const handleDragStart = (index: number) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const reordered = [...creators];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);

    dragItem.current = null;
    dragOverItem.current = null;

    setCreators(reordered);

    // Persist new order
    try {
      await apiRequest('/api/admin/boosted-creators/reorder', {
        method: 'POST',
        body: JSON.stringify({ userIds: reordered.map(c => c.userId) }),
      });
    } catch (err: any) {
      alert('Failed to save order. Refreshing...');
      await fetchBoostedCreators();
    }
  };

  // Move up/down helpers
  const moveItem = async (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= creators.length) return;

    const reordered = [...creators];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    setCreators(reordered);

    try {
      await apiRequest('/api/admin/boosted-creators/reorder', {
        method: 'POST',
        body: JSON.stringify({ userIds: reordered.map(c => c.userId) }),
      });
    } catch (err: any) {
      alert('Failed to save order. Refreshing...');
      await fetchBoostedCreators();
    }
  };

  if (loading) {
    return (
      <div className="page">
        <h1>Boosted Creators</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <h1>Boosted Creators</h1>
        <p style={{ color: 'var(--color-danger)' }}>Error: {error}</p>
        <button onClick={fetchBoostedCreators}>Retry</button>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h1 style={{ margin: 0 }}>Boosted Creators</h1>
        <button
          onClick={() => setShowModal(true)}
          style={{
            padding: '8px 20px', backgroundColor: 'var(--color-success-strong)', color: 'white',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
          }}
        >
          + Add Creator
        </button>
      </div>
      <p style={{ color: 'var(--color-muted)', marginBottom: '20px' }}>
        Creators in this list get a ranking boost. Drag rows or use arrows to reorder — #1 gets the highest boost.
      </p>

      {creators.length === 0 ? (
        <p style={{ color: 'var(--color-muted)' }}>No boosted creators yet. Click "Add Creator" to get started.</p>
      ) : (
        <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-hover)', borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ padding: '12px', textAlign: 'center', color: 'var(--color-text)', width: '50px' }}>#</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>User ID</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text)' }}>Added By</th>
                <th style={{ padding: '12px', textAlign: 'center', color: 'var(--color-text)', width: '100px' }}>Order</th>
                <th style={{ padding: '12px', textAlign: 'center', color: 'var(--color-text)', width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {creators.map((c, index) => (
                <tr
                  key={c._id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => e.preventDefault()}
                  style={{ borderBottom: '1px solid var(--color-border)', cursor: 'grab' }}
                >
                  <td style={{ padding: '12px', textAlign: 'center', color: 'var(--color-muted)', fontSize: '16px' }}>
                    ☰
                  </td>
                  <td style={{ padding: '12px', color: 'var(--color-text)', fontWeight: 500 }}>{c.name}</td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)', fontSize: '13px', fontFamily: 'monospace' }}>{c.userId}</td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)' }}>{c.addedBy || '-'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0}
                      style={{
                        padding: '2px 8px', marginRight: '4px', backgroundColor: 'transparent',
                        color: index === 0 ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)',
                        borderRadius: '4px', cursor: index === 0 ? 'default' : 'pointer',
                      }}
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveItem(index, 1)}
                      disabled={index === creators.length - 1}
                      style={{
                        padding: '2px 8px', backgroundColor: 'transparent',
                        color: index === creators.length - 1 ? 'var(--color-border-strong)' : 'var(--color-text)',
                        border: '1px solid var(--color-border)', borderRadius: '4px',
                        cursor: index === creators.length - 1 ? 'default' : 'pointer',
                      }}
                    >
                      ▼
                    </button>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleRemove(c.userId)}
                      disabled={processingId === c.userId}
                      style={{
                        padding: '6px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white',
                        border: 'none', borderRadius: '4px',
                        cursor: processingId === c.userId ? 'not-allowed' : 'pointer',
                        opacity: processingId === c.userId ? 0.6 : 1,
                      }}
                    >
                      {processingId === c.userId ? '...' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Search Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={closeSearchModal}
        >
          <div
            style={{
              backgroundColor: 'var(--color-surface-secondary)', borderRadius: '12px', width: '500px',
              maxHeight: '70vh', display: 'flex', flexDirection: 'column',
              border: '1px solid var(--color-border)', overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
              <h2 style={{ margin: '0 0 12px 0', color: 'var(--color-text)', fontSize: '18px' }}>Add Boosted Creator</h2>
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Search by name, email, or user ID..."
                style={{
                  width: '100%', padding: '10px 14px', backgroundColor: 'var(--color-input)',
                  border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)',
                  fontSize: '14px', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
              {searching && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>Searching...</div>
              )}

              {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>No users found</div>
              )}

              {!searching && searchQuery.trim().length < 2 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Type at least 2 characters to search
                </div>
              )}

              {searchResults.map(user => {
                const alreadyAdded = creators.some(c => c.userId === user.userId);
                return (
                  <div
                    key={user.userId}
                    style={{
                      padding: '12px 20px', borderBottom: '1px solid var(--color-surface-hover)',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt=""
                          style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--color-border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--color-muted)', fontSize: '14px',
                        }}>
                          {(user.name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: 'var(--color-text)', fontWeight: 500 }}>{user.name}</div>
                        <div style={{ color: 'var(--color-muted)', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user.email || user.userId}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddCreator(user)}
                      disabled={alreadyAdded || adding === user.userId}
                      style={{
                        padding: '6px 16px', marginLeft: '12px', flexShrink: 0,
                        backgroundColor: alreadyAdded ? 'var(--color-border)' : 'var(--color-success-strong)',
                        color: alreadyAdded ? 'var(--color-muted)' : 'white',
                        border: 'none', borderRadius: '4px', fontSize: '13px',
                        cursor: alreadyAdded || adding === user.userId ? 'default' : 'pointer',
                        opacity: adding === user.userId ? 0.6 : 1,
                      }}
                    >
                      {alreadyAdded ? 'Added' : adding === user.userId ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border)', textAlign: 'right' }}>
              <button
                onClick={closeSearchModal}
                style={{
                  padding: '8px 16px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)',
                  border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
